import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface DesignationRecord {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export type DesignationDraft = Pick<DesignationRecord, "name" | "description">;

const LS_KEY = "inventory.designations.v1";
const BOOT_KEY = "inventory.designations.demoSeeded.v1";

/** Demo rows when the API is unavailable or returns no data (matches employee screen samples). */
const STATIC_DEMO_DESIGNATIONS: DesignationRecord[] = (() => {
  const t = "2023-01-01T00:00:00.000Z";
  const now = new Date().toISOString();
  return [
    {
      id: "desig-demo-account-finance",
      name: "Account and Finance",
      description: "Accounting, bookkeeping, and financial reporting.",
      createdAt: t,
      updatedAt: now,
    },
    {
      id: "desig-demo-sales-officer",
      name: "Sales Officer",
      description: "Field and counter sales execution.",
      createdAt: t,
      updatedAt: now,
    },
    {
      id: "desig-demo-coordinator",
      name: "Coordinator",
      description: "Coordination across teams and schedules.",
      createdAt: t,
      updatedAt: now,
    },
    {
      id: "desig-demo-hr-manager",
      name: "HR Manager",
      description: "Human resources management and compliance.",
      createdAt: t,
      updatedAt: now,
    },
    {
      id: "desig-demo-ops-lead",
      name: "Operations Lead",
      description: "Leads operational workflows and quality.",
      createdAt: t,
      updatedAt: now,
    },
  ];
})();

type DesignationApiRecord = {
  id?: number | string;
  name: string;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type UpsertDesignationApiResponse = {
  message?: string;
  data?: DesignationApiRecord;
};

type DeleteDesignationApiResponse = {
  message?: string;
};

type UpsertDesignationResult = {
  record: DesignationRecord;
  message: string;
};

type DeleteDesignationResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: DesignationRecord[]): DesignationRecord[] {
  const byId = new Map<ID, DesignationRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function extractRowsFromResponse(body: unknown): DesignationApiRecord[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): DesignationApiRecord[] | null => {
    if (Array.isArray(block)) return block as DesignationApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as DesignationApiRecord[];
    }
    return null;
  };

  for (const key of ["data", "designations", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

/** Resolves create/update JSON whether the resource is at `data`, `data.designation`, or root. */
function extractDesignationFromUpsertBody(body: unknown): DesignationApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.name === "string") {
      return block as unknown as DesignationApiRecord;
    }
    const nested = block.designation;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested as unknown as DesignationApiRecord;
    }
  }
  const topDes = root.designation;
  if (topDes && typeof topDes === "object" && !Array.isArray(topDes)) {
    return topDes as unknown as DesignationApiRecord;
  }
  if (typeof root.name === "string" && "id" in root) {
    return root as unknown as DesignationApiRecord;
  }
  return null;
}

function mapApiRecord(record: DesignationApiRecord): DesignationRecord {
  const created =
    record.created_at || record.createdAt || new Date().toISOString();
  const updated = record.updated_at || record.updatedAt || created;
  return {
    id: String(record.id ?? ""),
    name: record.name,
    description: (record.description ?? "").toString(),
    createdAt: created,
    updatedAt: updated,
  };
}

function buildApiPayload(draft: DesignationDraft) {
  return {
    name: draft.name.trim(),
    description: (draft.description ?? "").trim() || null,
  };
}

function load(): DesignationRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DesignationRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: DesignationRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listDesignations(): DesignationRecord[] {
  return load().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getDesignationById(id: ID): DesignationRecord | undefined {
  return load().find((d) => d.id === id);
}

export function seedDesignationsIfEmpty() {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(BOOT_KEY)) return;
  if (load().length > 0) {
    localStorage.setItem(BOOT_KEY, "1");
    return;
  }
  save(STATIC_DEMO_DESIGNATIONS);
  localStorage.setItem(BOOT_KEY, "1");
}

/** Ensures there is data to show when sync cannot load from the API. */
function ensureDesignationsDemoFallback() {
  if (load().length > 0) return;
  save(STATIC_DEMO_DESIGNATIONS);
  if (typeof localStorage !== "undefined") localStorage.setItem(BOOT_KEY, "1");
}

export async function syncDesignationsFromApi() {
  try {
    const collected: DesignationApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/designations", {
        params: { page, per_page: 200 },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(
      collected.map(mapApiRecord).filter((r) => Boolean(r.id)),
    );
    save(records);
    return listDesignations();
  } catch (error) {
    console.error("[Designations] syncDesignationsFromApi failed", error);
    ensureDesignationsDemoFallback();
    return listDesignations();
  }
}

export async function createDesignation(draft: DesignationDraft): Promise<UpsertDesignationResult> {
  const items = load();
  const payload = buildApiPayload(draft);
  const response = await api.post<UpsertDesignationApiResponse>("/api/v1/designations", payload);
  const created = extractDesignationFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create designation response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Designation created successfully." };
}

export async function updateDesignation(id: ID, draft: DesignationDraft): Promise<UpsertDesignationResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;
  const payload = buildApiPayload(draft);
  const response = await api.put<UpsertDesignationApiResponse>(`/api/v1/designations/${id}`, payload);
  const updated = extractDesignationFromUpsertBody(response.data);
  if (!updated) {
    throw new Error("Invalid update designation response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Designation updated successfully." };
}

export async function deleteDesignation(id: ID): Promise<DeleteDesignationResult> {
  const response = await api.delete<DeleteDesignationApiResponse>(`/api/v1/designations/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Designation deleted successfully." };
}
